import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dashboard from 'domain/entity/dashboard';
import i18n from 'asset/translation';
import { useDashboard } from 'adapter/in/dashboards/context/Dashboard';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { isSome } from 'fp-ts/lib/Option';
import { DuplicateDashboardCriteriaBuilder } from 'use-case/dashboard/duplicate/duplicateDashboardCriteria';
import SingleNameInputModal, { CreateNameFormData } from 'adapter/in/common/components/SingleNameInputModal/SingleNameInputModal';
import gtmIds from 'adapter/in/dashboards/config/gtm';

const gtm = gtmIds.dashboard.duplicateModal;

export const useOnDuplicateDashboardModal = (onDuplicateSuccess: () => void, withRedirect: boolean) => {
    const { duplicateIsLoading, duplicateResult, duplicateError, duplicate, duplicateReset } = useDashboard();
    const { navigationPush } = useNavigation();
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [activeModalDashboard, setActiveModalDashboard] = useState<Dashboard>();

    const defaultValue = useMemo(() => i18n.t('dashboards.duplicate.modal.defaultName', { name: activeModalDashboard?.name ?? '' }), [
        activeModalDashboard,
    ]);

    useEffect(() => {
        if (isSome(duplicateResult)) {
            handleCloseModal();
            onDuplicateSuccess();
            duplicateReset();
            if (withRedirect) {
                navigationPush(`${duplicateResult.value.id}`);
            }
        }
    }, [duplicateResult]);

    const handleCloseModal = () => {
        setShowDuplicateModal(false);
    };

    const handleOnDuplicate = (dashboard: Dashboard) => {
        setActiveModalDashboard(dashboard);
        setShowDuplicateModal(true);
    };

    const handleFormSubmit = useCallback(
        (data: CreateNameFormData) => {
            if (activeModalDashboard) {
                duplicate(DuplicateDashboardCriteriaBuilder.create().withId(activeModalDashboard.id).withName(data.name).build());
            }
        },
        [duplicate, activeModalDashboard]
    );

    const onDuplicateModal = (
        <SingleNameInputModal
            showModal={showDuplicateModal}
            success={!!isSome(duplicateResult)}
            handleCloseModal={handleCloseModal}
            handleOnSubmit={handleFormSubmit}
            isLoading={duplicateIsLoading}
            error={isSome(duplicateError)}
            defaultValue={defaultValue}
            title={i18n.t('dashboards.duplicate.modal.title')}
            dataIdContext={{
                save: gtm.actions.duplicate,
                inputName: gtm.inputName,
                cancel: gtm.actions.cancel,
                tryAgain: gtm.actions.tryAgain,
            }}
            message={i18n.t('dashboards.duplicate.modal.name')}
            proceedButtonText={i18n.t('dashboards.duplicate.action.duplicateShort')}
        />
    );

    return { handleOnDuplicate, onDuplicateModal };
};
